/**
 * Utility functions for card-based UI components
 */

import { ProgressMessage } from '~/hooks/task/useTaskProgressMessages'
import { AgentTaskStatus, ProgressMessageType } from '~/protos/agents'

/**
 * Structured log entry for display in CollapsibleLogCard.
 * Keeps timestamp and message separate to avoid string parsing.
 */
export interface LogEntry {
  /** Formatted timestamp string like "15:52:49", or null if no timestamp */
  timestamp: string | null
  /** The log message content */
  message: string
}

/**
 * SourceFilesLoadingSlide (Code Documentation) state progression.
 * Logs should be cumulative - each state includes logs from all previous states.
 */
const SOURCE_FILES_STATE_PROGRESSION = [
  AgentTaskStatus.MIGRATION_ENDPOINTS_SELECTED,
  AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED
]

/**
 * Artifact1 slide state progression (in order).
 * Logs should be cumulative - each state includes logs from all previous states.
 */
const ARTIFACT1_STATE_PROGRESSION = [
  AgentTaskStatus.REFACTOR_PROCESSING_ENDPOINTS,
  AgentTaskStatus.REFACTOR_API_PROPOSING
]

/**
 * Artifact2 slide state progression (in order).
 * Logs should be cumulative - each state includes logs from all previous states.
 */
const ARTIFACT2_STATE_PROGRESSION = [
  AgentTaskStatus.REFACTOR_DOCS_GENERATING,
  AgentTaskStatus.REFACTOR_DOCS_GENERATED,
  AgentTaskStatus.REFACTOR_API_SPECS_GENERATED,
  AgentTaskStatus.REFACTOR_CODE_GENERATED
]

/**
 * Maps a current task status to the status(es) where logs should be extracted from.
 * For each slide's state progression, returns cumulative statuses (all previous states + current).
 * This enables showing all progress messages accumulated during a slide's lifecycle.
 *
 * State progressions:
 * - SourceFiles: MIGRATION_ENDPOINTS_SELECTED -> REFACTOR_EXISTING_DOCS_GENERATED
 * - Artifact1: REFACTOR_PROCESSING_ENDPOINTS -> REFACTOR_API_PROPOSING
 * - Artifact2: REFACTOR_DOCS_GENERATING -> REFACTOR_DOCS_GENERATED -> REFACTOR_API_SPECS_GENERATED -> REFACTOR_CODE_GENERATED
 *
 * @param currentStatus - The current task status
 * @returns Array of statuses to look for logs in (in chronological order)
 */
export function getLogSourceStatuses(currentStatus?: AgentTaskStatus): AgentTaskStatus[] {
  if (currentStatus === undefined) {
    return []
  }

  // Check if current status is in the SourceFiles (Code Documentation) progression
  const sourceFilesIndex = SOURCE_FILES_STATE_PROGRESSION.indexOf(currentStatus)
  if (sourceFilesIndex !== -1) {
    // Return all states up to and including the current state (cumulative logs)
    return SOURCE_FILES_STATE_PROGRESSION.slice(0, sourceFilesIndex + 1)
  }

  // Check if current status is in the Artifact1 progression
  const artifact1Index = ARTIFACT1_STATE_PROGRESSION.indexOf(currentStatus)
  if (artifact1Index !== -1) {
    // Return all states up to and including the current state (cumulative logs)
    return ARTIFACT1_STATE_PROGRESSION.slice(0, artifact1Index + 1)
  }

  // Check if current status is in the Artifact2 progression
  const artifact2Index = ARTIFACT2_STATE_PROGRESSION.indexOf(currentStatus)
  if (artifact2Index !== -1) {
    // Return all states up to and including the current state (cumulative logs)
    return ARTIFACT2_STATE_PROGRESSION.slice(0, artifact2Index + 1)
  }

  // Default: look for logs in the current status itself
  return [currentStatus]
}

/**
 * Formats a millisecond timestamp to HH:MM:SS format in user's local timezone.
 *
 * @param createdTsMs - Timestamp in milliseconds since epoch
 * @returns Formatted time string like "14:30:45" or null if no timestamp
 */
function formatTimestamp(createdTsMs: number | undefined): string | null {
  if (!createdTsMs) {
    return null
  }
  const date = new Date(createdTsMs)
  // Use local timezone for display
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const seconds = date.getSeconds().toString().padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

/**
 * Converts a ProgressMessage to a structured LogEntry.
 * Handles both INTERNAL_PROGRESS and CHECKPOINT message types.
 *
 * @param msg - The progress message object
 * @returns Structured LogEntry with separate timestamp and message
 */
function toLogEntry(msg: ProgressMessage): LogEntry {
  const timestamp = formatTimestamp(msg.createdTsMs)

  if (msg.messageType === ProgressMessageType.CHECKPOINT) {
    return {
      timestamp,
      message: msg.checkpointMsg || ''
    }
  }

  // INTERNAL_PROGRESS type - use title with optional sub-messages
  let message = msg.title || ''
  if (msg.internalMsgs && msg.internalMsgs.length > 0) {
    // Append sub-messages as additional lines
    message += '\n' + msg.internalMsgs.join('\n')
  }

  return { timestamp, message }
}

/**
 * Extracts and formats progress messages from the new database-backed progress messages.
 * Uses getLogSourceStatuses to determine which status(es) to include based on
 * the slide's state aggregation logic.
 *
 * @param progressMessages - Array of ProgressMessage objects from useTaskProgressMessages hook
 * @param currentStatus - The current task status (used to determine which states to aggregate)
 * @returns Array of structured LogEntry objects with timestamp and message
 */
export function extractProgressMessagesFromDB(
  progressMessages: ProgressMessage[] | undefined,
  currentStatus?: AgentTaskStatus
): LogEntry[] {
  if (!progressMessages || progressMessages.length === 0) {
    return []
  }

  const sourceStatuses = getLogSourceStatuses(currentStatus)

  // If no source statuses, return empty
  if (sourceStatuses.length === 0) {
    return []
  }

  // Convert status enums to their string names for comparison
  const statusNames = new Set(sourceStatuses.map(s => AgentTaskStatus[s]))

  // Filter messages by status and convert to LogEntry
  const entries: LogEntry[] = []
  for (const msg of progressMessages) {
    // Skip messages without a valid status or not matching our target statuses
    if (!msg.taskStatus || !statusNames.has(msg.taskStatus)) {
      continue
    }

    const entry = toLogEntry(msg)
    if (entry.message) {
      entries.push(entry)
    }
  }

  return entries
}
